/**
 * LINE entity-kind group-code reader family — extracted from
 * entityParser.ts (R17 A12A-DXF-ENTITY-PARSER-REGISTRY-SEAM).
 *
 * Pure structural move: the exact same six closures (group codes
 * 10/20/30/11/21/31 -> start/end point components) that lived inline in
 * entityParser.ts now live here and are imported back to fill the `LINE`
 * slot of the untouched `ENTITY_GROUP_READERS` dispatch/registration table.
 * This is the first representative family moved through the seam; the
 * registry itself, its keys, and its call graph are unchanged.
 *
 * This module imports the shared low-level parsing primitives from
 * ./entityParser.groupValueHelpers (a dependency-free leaf) rather than
 * from entityParser.ts itself, keeping the dependency edge one-directional
 * — entityParser.ts imports `LINE_GROUP_READERS` from here, and nothing
 * flows the other way, so no cycle is introduced.
 *
 * `LineFamilyDraft` is a local structural "slice" of `SupportedEntityDraft`
 * (entityParser.ts): the real draft has an identical `points` field shape,
 * so it is assignable here without importing the full draft type — the
 * same technique already used by entityParser.hatchBoundary.ts.
 */

import {
  type MutablePoint,
  setPointComponent,
} from "./entityParser.groupValueHelpers";

/** The slice of `SupportedEntityDraft` (entityParser.ts) this family reads. */
type LineFamilyDraft = {
  points: Record<string, MutablePoint>;
};

type LineFamilyGroupReader = (draft: LineFamilyDraft, value: string) => void;

// AcDbLine group codes: 10/20/30 start point, 11/21/31 end point.
export const LINE_GROUP_READERS: Record<number, LineFamilyGroupReader> = {
  10: (draft, value) => {
    setPointComponent(draft.points, "start", "x", 10, value);
  },
  20: (draft, value) => {
    setPointComponent(draft.points, "start", "y", 20, value);
  },
  30: (draft, value) => {
    setPointComponent(draft.points, "start", "z", 30, value);
  },
  11: (draft, value) => {
    setPointComponent(draft.points, "end", "x", 11, value);
  },
  21: (draft, value) => {
    setPointComponent(draft.points, "end", "y", 21, value);
  },
  31: (draft, value) => {
    setPointComponent(draft.points, "end", "z", 31, value);
  }
};
